import React, { useState, useEffect } from 'react';
import offlineStorageManager from '../utils/offlineStorageManager';

/**
 * Offline Status Indicator Component
 * Shows network status and sync status to users
 */
const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check initial online status
    setIsOnline(navigator.onLine);

    // Listen for online/offline changes
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingRequests();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    // Watch for pending requests
    const checkPending = async () => {
      const requests = await offlineStorageManager.getPendingRequests();
      setPendingRequests(requests.length);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check pending requests every 5 seconds
    const interval = setInterval(checkPending, 5000);
    checkPending();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const syncPendingRequests = async () => {
    const requests = await offlineStorageManager.getPendingRequests();
    // In a real app, you would sync these requests here
    console.log(`Syncing ${requests.length} pending requests`);
  };

  if (isOnline && pendingRequests === 0) {
    return null; // Don't show indicator if online and no pending requests
  }

  return (
    <div className="fixed top-4 right-4 z-50">
      <div
        className={`rounded-lg shadow-lg p-4 cursor-pointer transition-all ${
          isOnline
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
        }`}
        onClick={() => setShowDetails(!showDetails)}
      >
        <div className="flex items-center gap-2">
          <div
            className={`w-3 h-3 rounded-full ${
              isOnline ? 'bg-green-500' : 'bg-red-500'
            } animate-pulse`}
          />
          <span
            className={`text-sm font-medium ${
              isOnline ? 'text-green-700' : 'text-red-700'
            }`}
          >
            {isOnline ? 'Online' : 'Offline'}
          </span>
          {pendingRequests > 0 && (
            <span className="ml-2 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded">
              {pendingRequests}
            </span>
          )}
        </div>

        {showDetails && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className={`text-xs mb-2 ${isOnline ? 'text-green-600' : 'text-red-600'}`}>
              {isOnline
                ? 'You are connected to the internet'
                : 'No internet connection. Your changes will be saved locally.'}
            </p>
            {pendingRequests > 0 && (
              <div className="text-xs text-yellow-700">
                <p className="font-semibold mb-1">
                  {pendingRequests} pending change{pendingRequests !== 1 ? 's' : ''}
                </p>
                <p>
                  {isOnline
                    ? 'Will sync automatically...'
                    : 'Will sync when you go online'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OfflineIndicator;
